import React from 'react';
import {
  getHomeVisitProposals,
  shouldShowHomeVisitProposals,
  getBookedHomeVisit
} from '../../utils/homeVisitWorkflowUtils';

/**
 * HomeVisitProposalsSection - Visar hembesöksförslag från byggkonsult
 * Används i EstimatView för att visa och hantera hembesökstidsförslag
 * 
 * ✅ Stödjer både nya workflow-strukturen och gamla strukturen (bakåtkompatibelt)
 * 
 * @param {Object} homeVisitProposals - Byggkonsultens hembesöksförslag (DEPRECATED - använd procurementData)
 * @param {Object} procurementData - Procurement data med workflow
 * @param {string} acceptingHomeVisitProposal - ID för förslag som accepteras
 * @param {Function} onAcceptProposal - Callback när förslag accepteras
 * @param {Object} showHomeVisitReply - State för vilka svar som visas
 * @param {Object} homeVisitReplyText - Text för svar
 * @param {Function} onHomeVisitReply - Callback för att skicka svar
 * @param {boolean} sendingHomeVisitReply - Loading state
 * @param {Function} setShowHomeVisitReply - Setter för att visa/dölja svarsfält
 * @param {Function} setHomeVisitReplyText - Setter för svarstext
 * @param {boolean} expandedProposal - Om förslagssektionen är expanderad
 * @param {Function} onToggleProposal - Callback för att toggla förslag
 */
const HomeVisitProposalsSection = ({
  procurementData,
  acceptingHomeVisitProposal,
  onAcceptProposal,
  showHomeVisitReply,
  homeVisitReplyText,
  onHomeVisitReply,
  sendingHomeVisitReply,
  setShowHomeVisitReply,
  setHomeVisitReplyText,
  expandedProposal,
  onToggleProposal,
}) => {
  // ✅ Använd nya utility-funktioner
  const homeVisitProposals = getHomeVisitProposals(procurementData);
  const shouldShowProposals = shouldShowHomeVisitProposals(procurementData);
  const bookedHomeVisit = getBookedHomeVisit(procurementData);

  // Om hembesöket redan är bokat, visa ingenting (BookedHomeVisitSection hanterar det)
  if (bookedHomeVisit) {
    return null;
  }

  // Om kunden har accepterat ett förslag, visa bekräftelse
  if (homeVisitProposals?.customerResponse?.status === 'accepted') {
    const acceptedProposal = homeVisitProposals.customerResponse.acceptedProposal;
    return (
      <div className="mb-6 bg-green-50 rounded-lg shadow-sm border border-green-200 p-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div>
            <h4 className="text-sm font-medium text-green-900 mb-1">
              Du har accepterat hembesök
            </h4>
            <p className="text-sm text-green-700 mb-2">
              <strong>{acceptedProposal?.displayText || 'Tid accepterad'}</strong>
            </p>
            <p className="text-xs text-green-600">
              Byggkonsulten kommer att bekräfta bokningen och skicka en kalenderinbjudan till dig.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Visa endast hembesöksförslag (INTE bokad tid - det hanteras av BookedHomeVisitSection)
  if (!shouldShowProposals) {
    return null;
  }

  // Dölj hela sektionen om kunden redan har svarat (nekat förslagen)
  if (homeVisitProposals?.customerResponse?.status === 'replied') {
    return null;
  }

  return (
    <div className="mb-6 bg-white rounded-lg shadow-sm border border-purple-200">
      {/* Accordion header - klickbar */}
      <div
        className="flex items-center justify-between p-4 cursor-pointer select-none bg-purple-50 hover:bg-purple-100 transition-colors"
        onClick={onToggleProposal}
        role="button"
        tabIndex={0}
        aria-expanded={expandedProposal}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggleProposal();
          }
        }}
      >
        <h2 className="text-lg font-semibold text-purple-800">
          🏠 Hembesök
        </h2>
        <div className="flex items-center gap-3">
          <span className="text-sm text-purple-700 font-medium">
            {homeVisitProposals.proposals?.length || 0} föreslagna tider
          </span>
          <svg
            className={`w-5 h-5 text-purple-600 transition-transform duration-200 ${expandedProposal ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Accordion content - expanderbar */}
      {expandedProposal && (
        <div className="p-4 bg-purple-50 border-t border-purple-200">
          <p className="text-sm text-purple-800 mb-3">
            {homeVisitProposals.message || 'Byggkonsulten har föreslagit följande tidpunkter för hembesök:'}
          </p>

          <div className="space-y-2">
            {homeVisitProposals.proposals.map((proposal, index) => (
              <div key={index} className="bg-white p-3 rounded border border-purple-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-sm font-medium text-gray-900">
                      {proposal.displayText}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowHomeVisitReply(prev => ({ ...prev, [index]: !prev[index] }));
                      }}
                      className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white text-xs font-medium rounded transition-colors"
                    >
                      {showHomeVisitReply[index] ? 'Ångra' : 'Neka'}
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onAcceptProposal(proposal);
                      }}
                      disabled={acceptingHomeVisitProposal === proposal.displayText}
                      className="px-3 py-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white text-xs font-medium rounded"
                    >
                      {acceptingHomeVisitProposal === proposal.displayText ? 'Accepterar...' : 'Acceptera'}
                    </button>
                  </div>
                </div>

                {/* Svarsfält */}
                {showHomeVisitReply[index] && (
                  <div className="mt-3 space-y-2">
                    <textarea
                      value={homeVisitReplyText[index] || ''}
                      onChange={(e) => setHomeVisitReplyText(prev => ({ ...prev, [index]: e.target.value }))}
                      placeholder="Skriv varför du inte kan ta hembesöket dessa tider..."
                      className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-leadme-pink focus:border-leadme-pink"
                      rows={3}
                    />
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onHomeVisitReply(index);
                      }}
                      disabled={sendingHomeVisitReply || !homeVisitReplyText[index]?.trim()}
                      className="px-4 py-2 bg-leadme-pink hover:bg-leadme-pink-dark disabled:bg-gray-400 disabled:cursor-not-allowed text-white text-sm font-medium rounded"
                    >
                      {sendingHomeVisitReply ? 'Skickar...' : 'Skicka svar'}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {homeVisitProposals.sentAt && (
            <p className="text-xs text-purple-600 mt-3">
              Skickat: {new Date(homeVisitProposals.sentAt).toLocaleString('sv-SE')}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default HomeVisitProposalsSection;

